/* eslint-disable import/no-anonymous-default-export */
import RefundBadge from "../components/RefundBadge";
import UrlListCell from "../components/UrlListCell";

export default {
  id: "adReviewDelete",
  title: "리뷰 삭제 신청",

  api: {
    list: {
      method: "GET",
      url: "/advertisement/admin/reviewDeletes",
      version: 2,
      paramsTransformer: (params) => {
        const p = { ...params };
        if (p.status === "전체") delete p.status;
        if (p.submitted === "전체") delete p.submitted;
        if (p.paid === "전체") delete p.paid;
        return p;
      },
    },
    count: {
      method: "GET",
      url: "/advertisement/admin/reviewDeletes/count",
      version: 2,
      paramsTransformer: (params) => {
        const p = { ...params };
        if (p.status === "전체") delete p.status;
        if (p.submitted === "전체") delete p.submitted;
        if (p.paid === "전체") delete p.paid;
        return p;
      },
    },
    submitted: {
      method: "POST",
      url: "/advertisement/admin/reviewDeletes/submitted",
    },
    paid: {
      method: "POST",
      url: "/advertisement/admin/reviewDeletes/paid",
    },
    complete: {
      method: "POST",
      url: "/advertisement/admin/reviewDeletes/complete",
    },
    delete: {
      method: "DELETE",
      url: "/advertisement/admin/reviewDeletes",
    },
  },

  filters: {
    default: {
      keyword: "",
      submitDate: "",
      completionDate: "",
      status: "전체",
      submitted: "전체",
      paid: "전체",
      page: 1,
      limit: 50,
    },
    schema: [
      {
        name: "keyword",
        label: "검색",
        type: "text",
        placeholder: "신청자, 플레이스명",
      },
      {
        name: "submitDate",
        label: "접수일",
        type: "date",
      },
      {
        name: "completionDate",
        label: "완료일",
        type: "date",
      },
      {
        name: "status",
        label: "상태",
        type: "select",
        options: [
          { value: "전체", label: "전체" },
          { value: "waiting", label: "대기중" },
          { value: "processing", label: "처리중" },
          { value: "completed", label: "완료" },
          { value: "partially_refunded", label: "부분환불" },
          { value: "refunded", label: "환불완료" },
        ],
      },
      {
        name: "submitted",
        label: "접수여부",
        type: "select",
        options: [
          { value: "전체", label: "전체" },
          { value: "complete", label: "접수완료" },
          { value: "notComplete", label: "미접수" },
        ],
      },
      {
        name: "paid",
        label: "결제상태",
        type: "select",
        options: [
          { value: "전체", label: "전체" },
          { value: "complete", label: "결제완료" },
          { value: "notComplete", label: "미결제" },
          { value: "refund", label: "환불" },
        ],
      },
    ],
  },

  summary: {
    fields: [
      { key: "totalCount", label: "총 신청" },
      { key: "waitingCount", label: "대기중" },
      { key: "processingCount", label: "처리중" },
      { key: "completedCount", label: "완료" },
      { key: "partiallyRefundedCount", label: "부분환불" },
      { key: "refundedCount", label: "환불완료" },
    ],
  },

  table: {
    keyField: "adCampaignMetaId",
    rowClick: {
      enabled: true,
      handler: () => {},
    },
    columns: [
      {
        key: "adCampaignMetaId",
        label: "메타아이디",
        center: true,
      },
      {
        key: "createdAt",
        label: "캐랭접수시간",
        center: true,
        render: (row) => {
          if (!row.createdAt) return "";
          const [date, time] = row.createdAt.split(" ");
          return (
            <>
              <div>{date}</div>
              <div>{time?.slice(0, 8)}</div>
            </>
          );
        },
      },
      {
        key: "userName",
        label: "신청자(ID)",
        center: true,
        render: (row) =>
          `${row.userName}${row.userId ? ` (${row.userId})` : ""}`,
      },
      {
        key: "placeName",
        label: "사업자명",
        center: true,
      },
      {
        key: "urls",
        label: "URL",
        center: true,
        render: (row) => <UrlListCell urls={row.urls || []} />,
      },
      {
        key: "urlCount",
        label: "등록건수",
        center: true,
        render: (row) => row.urlCount ?? (row.urls || []).length,
      },
      {
        key: "totalCost",
        label: "결제금액",
        center: true,
        render: (row) => `${(row.totalCost ?? 0).toLocaleString()}원`,
      },
      {
        key: "refundedAmount",
        label: "환불금액",
        center: true,
        render: (row) => `${(row.refundedAmount ?? 0).toLocaleString()}원`,
      },
      {
        key: "status",
        label: "상태",
        center: true,
        render: (row) => (
          <span className={`badge badge-${row.status}`}>
            {getStatusLabel(row.status)}
          </span>
        ),
      },
      {
        key: "paymentType",
        label: "결제방법\n결제상태",
        center: true,
        render: (row) => {
          const paymentLabel = getPaymentLabel(row.paymentType);
          const isRefunded =
            row.status === "partially_refunded" || row.status === "refunded";
          return (
            <>
              <div>{paymentLabel}</div>
              <div>
                {isRefunded ? (
                  <RefundBadge
                    refundedAmount={row.refundedAmount ?? 0}
                    remainingAmount={row.remainingAmount ?? 0}
                    refundDate={row.refundedAt}
                  />
                ) : (
                  <span
                    className={`badge badge-${row.isPaid === 1 ? "결제완료" : "미결제"}`}
                  >
                    {row.isPaid === 1 ? "결제완료" : "미결제"}
                  </span>
                )}
              </div>
            </>
          );
        },
      },
      {
        key: "report",
        label: "리포트",
        center: true,
        render: (row, handlers) => (
          <button
            type="button"
            className="status-btn submitted"
            onClick={(e) => {
              e.stopPropagation();
              if (handlers?.openReportModal) {
                handlers.openReportModal(row.adCampaignMetaId, row.placeName);
              }
            }}
          >
            리포트
          </button>
        ),
      },
      {
        key: "notification",
        label: "알림톡",
        center: true,
        render: (row) => (
          <button
            type="button"
            className="notification-btn"
            data-campaign-meta-id={row.adCampaignMetaId}
            data-action="notification"
            data-user-id={row.userId}
            data-product-name="악성 리뷰 삭제"
            data-submitted-at={row.createdAt || ""}
            data-purchase-amount={row.totalCost ?? 0}
            onClick={(e) => e.stopPropagation()}
          >
            알림톡
          </button>
        ),
      },
      {
        key: "memo",
        label: "메모",
        center: true,
        render: (row) => (
          <button
            type="button"
            className={row.memo ? "memo-btn has-memo" : "memo-btn"}
            data-action="memo"
            data-campaign-meta-id={row.adCampaignMetaId}
            data-memo={row.memo || ""}
            onClick={(e) => e.stopPropagation()}
          >
            {row.memo ? (
              <span className="memo-preview">{row.memo}</span>
            ) : (
              "메모"
            )}
          </button>
        ),
      },
    ],
  },

  actions: [
    {
      id: "submitted",
      label: "접수/취소",
      actionType: "submitted",
      enabledWhen: (selectedIds) => selectedIds.length > 0,
      payloadBuilder: (selectedIds, items) => {
        const selectedMetas = items.filter((item) =>
          selectedIds.includes(item.adCampaignMetaId)
        );
        // 미접수 URL이 하나라도 있으면 → 전체 접수(미접수 URL만 전송)
        // 전체 접수된 경우 → 전체 접수취소(접수 URL만 전송)
        const hasUnsubmitted = selectedMetas.some((meta) =>
          (meta.urls || []).some(
            (url) => !url.isSubmitted && url.status !== "refunded"
          )
        );
        return {
          adReviewDeleteIds: selectedMetas.flatMap((meta) =>
            (meta.urls || [])
              .filter((url) => url.status !== "refunded")
              .filter((url) =>
                hasUnsubmitted ? !url.isSubmitted : url.isSubmitted
              )
              .map((url) => url.adReviewDeleteId)
          ),
        };
      },
    },
    {
      id: "paid",
      label: "결제완료",
      actionType: "paid",
      enabledWhen: (selectedIds, items) => {
        if (selectedIds.length === 0) return false;
        // 미결제 Meta가 하나라도 있을 때만 활성화
        return items.some(
          (item) =>
            selectedIds.includes(item.adCampaignMetaId) && item.isPaid !== 1,
        );
      },
      // BE: URL id 하나만 보내면 해당 Meta의 결제 상태를 토글
      payloadBuilder: (selectedIds, items) => ({
        adReviewDeleteIds: items
          .filter(
            (item) =>
              selectedIds.includes(item.adCampaignMetaId) && item.isPaid !== 1
          )
          .map((item) => item.urls?.[0]?.adReviewDeleteId)
          .filter(Boolean),
      }),
    },
    {
      id: "complete",
      label: "처리완료/취소",
      actionType: "complete",
      enabledWhen: (selectedIds) => selectedIds.length > 0,
      payloadBuilder: (selectedIds, items) => {
        const selectedMetas = items.filter((item) =>
          selectedIds.includes(item.adCampaignMetaId)
        );
        // 미완료 URL이 하나라도 있으면 → 전체 완료(미완료 URL만 전송)
        // 전체 완료된 경우 → 전체 완료취소(완료 URL만 전송)
        const hasUncompleted = selectedMetas.some((meta) =>
          (meta.urls || []).some(
            (url) => !url.isCompleted && url.status !== "refunded"
          )
        );
        return {
          adReviewDeleteIds: selectedMetas.flatMap((meta) =>
            (meta.urls || [])
              .filter((url) => url.status !== "refunded")
              .filter((url) =>
                hasUncompleted ? !url.isCompleted : url.isCompleted
              )
              .map((url) => url.adReviewDeleteId)
          ),
        };
      },
    },
    {
      id: "refund",
      label: "환불/취소",
      actionType: "refund",
      requires: "ad.review_delete.refund", // 권한 없으면 버튼 숨김 (R-FE-04)
      enabledWhen: (selectedIds, items) => {
        if (selectedIds.length === 0) return false;
        // v2: isPaid는 0/1. 부분환불 Meta도 isPaid=1 유지 가능성 있으므로 2도 허용
        return items.some(
          (item) =>
            selectedIds.includes(item.adCampaignMetaId) &&
            (item.isPaid === 1 || item.isPaid === 2) &&
            (item.remainingAmount ?? 0) > 0,
        );
      },
    },
    {
      id: "delete",
      label: "삭제",
      actionType: "delete",
      enabledWhen: (selectedIds) => selectedIds.length > 0,
      confirm: () => "선택한 신청을 삭제하시겠습니까?",
      payloadBuilder: (selectedIds, items) => ({
        reviewDeletes: items
          .filter((item) => selectedIds.includes(item.adCampaignMetaId))
          .flatMap((item) =>
            (item.urls || []).map((url) => ({
              adReviewDeleteId: url.adReviewDeleteId,
            }))
          ),
      }),
    },
    {
      id: "attachmentDownload",
      label: "첨부자료 다운로드",
      actionType: "attachmentDownload",
      enabledWhen: (selectedIds) => selectedIds.length > 0,
      payloadBuilder: (selectedIds, items) => ({
        rows: items.filter((item) =>
          selectedIds.includes(item.adCampaignMetaId)
        ),
      }),
    },
  ],

  download: {
    type: "xlsx",
    fileName: "리뷰삭제_신청자료.xlsx",
    sheetName: "리뷰삭제 신청",
    transformRow: (row) => ({
      "메타 ID": row.adCampaignMetaId,
      신청일: row.createdAt,
      신청자: `${row.userName}${row.userId ? `(${row.userId})` : ""}`,
      플레이스명: row.placeName,
      URL: (row.urls || [])
        .map((u) => u.reviewUrl)
        .filter(Boolean)
        .join("\n"),
      "등록 건수": row.urlCount ?? (row.urls || []).length,
      결제금액: row.totalCost ?? 0,
      환불금액: row.refundedAmount ?? 0,
      상태: getStatusLabel(row.status),
    }),
  },

  // 사업자등록증 + 리뷰 이미지 zip (별도 버튼)
  attachmentDownload: {
    type: "zip",
    fileName: "리뷰삭제_첨부자료.zip",
    groupBy: (row) => row.adCampaignMetaId,
    folderNameBuilder: (groupKey, groupRows) => {
      if (groupRows?.length > 0) {
        const r = groupRows[0];
        return `[${getStatusLabel(r.status)}]${r.placeName}_${r.userName}`;
      }
      return String(groupKey);
    },
    urlTextBuilder: (row) =>
      (row.urls || [])
        .map((u) => u.reviewUrl)
        .filter(Boolean)
        .join("\n"),
    assets: (row) => {
      const list = [];
      if (row.businessRegistrationUrl) {
        list.push({ name: "사업자등록증.jpg", url: row.businessRegistrationUrl });
      }
      if (Array.isArray(row.reviewImgUrls)) {
        row.reviewImgUrls.forEach((url, i) => {
          list.push({ name: `악성리뷰_${i + 1}.jpg`, url });
        });
      }
      return list;
    },
  },
};

export function getStatusLabel(status) {
  switch (status) {
    case "waiting":
      return "대기중";
    case "processing":
      return "처리중";
    case "completed":
      return "완료";
    case "partially_refunded":
      return "부분환불";
    case "refunded":
      return "환불완료";
    case "deleted":
      return "삭제";
    default:
      return status || "-";
  }
}

export function getPaymentLabel(paymentType) {
  switch (paymentType) {
    case "card":
      return "카드";
    case "v_bank":
    case "vbank":
      return "가상계좌";
    case "account":
      return "계좌이체";
    case "iap":
      return "앱결제";
    case "point":
      return "포인트 결제";
    default:
      return paymentType || "-";
  }
}
